// InfoHUD component - top bar with logo, zoom controls, and share container
import type { InfoHUDConfig, InfoHUDAPI } from "@/types/stage-types.js";
import { createIcon } from "@UI/Icon/index.js";
import {
  createContentMainContainer,
  type ContentMainAPI,
} from "./subcomponents/ContentMainContainer/index.js";
import {
  createZoomControlsInContainer,
  type ZoomControlsAPI,
} from "./subcomponents/ZoomControls/index.js";
import {
  createStatusHUDInContainer,
  type StatusHUDAPI,
} from "./subcomponents/StatusHUD/index.js";
import {
  createHelpersInContainer,
  type HelpersAPI,
} from "./subcomponents/Helpers/index.js";
import type { HelperType } from "./subcomponents/Helpers/Helpers.js";
import type { InfoHUDVisibilityConfig } from "../../visibility-profiles";
// Languages and Themes are orchestrated by ContentMainContainer
// Dropdown and ModalManager not needed here; handled inside subcomponents
// Separator management handled inside ContentMainContainer
import {
  createShareContainerInContainer,
  type ShareContainerAPI,
} from "./subcomponents/ShareContainer/index.js";
import { logger } from "@/utils/logger.ts";
import type { HandednessStateAPI } from "@/state/handedness";

/**
 * Creates the InfoHUD component with logo, zoom controls, and share container
 * @param container - Container element for the InfoHUD
 * @param config - Configuration with event handlers
 * @returns InfoHUD API
 */
export const createInfoHUD = (
  container: HTMLElement,
  config: InfoHUDConfig = {}
): InfoHUDAPI => {
  console.log("🎯 InfoHUD: Creating with config:", config);
  let isDestroyed = false;
  let contentMainAPI: ContentMainAPI | null = null;
  let zoomControlsAPI: ZoomControlsAPI | null = null;
  let statusHUDAPI: StatusHUDAPI | null = null;
  let helpersAPI: HelpersAPI | null = null;
  let shareContainerAPI: ShareContainerAPI | null = null;
  let zoomContainerEl: HTMLElement | null = null;
  let statusContainerEl: HTMLElement | null = null;
  let contentMainContainerEl: HTMLElement | null = null;
  let helpersContainerEl: HTMLElement | null = null;
  let currentHelpersAllowed: HelperType[] | null = null;
  let rootElement: HTMLElement | null = null;
  let unsubscribeHandedness: (() => void) | null = null;
  const handednessState: HandednessStateAPI | null =
    (config.handedness as HandednessStateAPI | null) ??
    (config.appStateManager?.handedness as HandednessStateAPI | null) ??
    null;

  let currentHandedness: "left" | "right" =
    (handednessState?.get?.() as "left" | "right" | undefined) ?? "right";

  const applyHandednessClass = (value: "left" | "right") => {
    currentHandedness = value;
    if (!rootElement) return;
    rootElement.dataset.handedness = value;
    rootElement.classList.toggle("is-left-handed", value === "left");
    rootElement.classList.toggle("is-right-handed", value !== "left");
  };

  /**
   * Initialize InfoHUD HTML structure
   */
  const initInfoHUD = (): void => {
    container.innerHTML = `
      <div class="info-hud">
        <!-- info: horizontal row with logo, content, share_container -->
        <div class="info">
          <div class="logo">
            <!-- Logo will be inserted here -->
            <div class="logo-icon"></div>
          </div>
          <div class="content">
            <!-- zoom: 86px x 32px -->
            <div class="info-left-side-container">
              <div class="zoom-container"></div>
              <!-- status: auto width -->
              <div class="status-container"></div>
              <!-- content: 156px x 32px -->
            </div>
            <div class="content-main"></div>
            <!-- helpers: 86px x 32px -->
            <div class="helpers-container"></div>
          </div>
          <div class="share-container">
            <!-- Share: empty for now -->
          </div>
        </div>
        <!-- limit_gradient: 1px height div with gradient -->
        <div class="limit-gradient"></div>
      </div>
    `;

    rootElement = container.querySelector(".info-hud") as HTMLElement;
    applyHandednessClass(currentHandedness);

    // Create and insert the logo icon using new container pattern
    const logoIconContainer = container.querySelector(
      ".logo-icon"
    ) as HTMLElement;
    if (logoIconContainer) {
      try {
        const logoIcon = createIcon({
          name: "unna_logo",
          size: 48, // Container will handle sizing
          useContainer: true, // Use new container pattern
        });

        // Clear existing content and append new icon
        logoIconContainer.innerHTML = "";
        logoIconContainer.appendChild(logoIcon.getElement());
      } catch (error) {
        console.warn("Failed to create unna_logo icon:", error);
        // Fallback text placeholder
        logoIconContainer.innerHTML = '<span class="logo-fallback">UNNA</span>';
      }
    }

    // Initialize ZoomControls component
    const zoomContainer = container.querySelector(
      ".zoom-container"
    ) as HTMLElement;
    zoomContainerEl = zoomContainer;
    if (zoomContainer) {
      try {
        const uiState = config.appStateManager?.uiState;
        if (!uiState) {
          console.warn("No UI state manager available for ZoomControls");
          return;
        }

        zoomControlsAPI = createZoomControlsInContainer(zoomContainer, {
          uiState: uiState,
          onZoomAction: (mode) => {
            console.log(`Zoom action triggered: ${mode}`);
            // Here we could calculate the actual zoom value and update the UI state
            // For now, we'll just trigger an update
            if (config.onZoomAction) {
              config.onZoomAction(mode);
            }
          },
        });
      } catch (error) {
        console.error("Failed to create ZoomControls component:", error);
      }
    }

    // Initialize StatusHUD component
    const statusContainer = container.querySelector(
      ".status-container"
    ) as HTMLElement;
    statusContainerEl = statusContainer;
    if (statusContainer) {
      try {
        statusHUDAPI = createStatusHUDInContainer(statusContainer, {
          initialStates: {
            mic: false,
            llm: false,
            shift: false,
            alt: false,
          },
          uiState: config.appStateManager?.uiState,
        });
      } catch (error) {
        console.error("Failed to create StatusHUD component:", error);
      }
    }

    // Create and insert the orchestrated content block (breakpoints/languages/themes)
    const contentMainContainer = container.querySelector(
      ".content-main"
    ) as HTMLElement;
    contentMainContainerEl = contentMainContainer;
    if (contentMainContainer) {
      try {
        const uiState = config.appStateManager?.uiState;
        const currentBreakpoint =
          uiState?.getState().current_breakpoint || "desktop";
        const currentTheme = uiState?.getState().theme || "light";
        const currentLanguage = uiState?.getState().current_language || "en";
        // Clear existing content and mount orchestrator
        contentMainContainer.innerHTML = "";
        contentMainAPI = createContentMainContainer({
          selectedBreakpoint: currentBreakpoint as any,
          selectedTheme: currentTheme as any,
          selectedLanguage: currentLanguage as any,
          availableLanguages: [currentLanguage as any],
          onBreakpointChange: (breakpoint) => {
            const uiState = config.appStateManager?.uiState;
            if (uiState) uiState.setBreakpoint(breakpoint as any);
            if (config.appStateManager?.appContext?.eventBus) {
              config.appStateManager.appContext.eventBus.emit(
                "breakpoint-changed",
                { breakpoint }
              );
            }
          },
          onThemeChange: (theme) => {
            const uiState = config.appStateManager?.uiState;
            const store = (config as any)?.appStateManager?.store;
            const getScreenPath =
              (config as any)?.appStateManager?.getScreenStatePath ||
              (() => null);
            try {
              const p = getScreenPath("theme");
              if (store && p) {
                store.set(p, theme as any);
              } else if (uiState) {
                uiState.setTheme(theme as any);
              }
            } catch {
              uiState?.setTheme(theme as any);
            }
          },
          onLanguageChange: (language) => {
            const uiState = config.appStateManager?.uiState;
            if (uiState) uiState.setLanguage(language as any);
          },
        });
        contentMainContainer.appendChild(contentMainAPI.getElement());
      } catch (error) {
        console.error("Failed to create breakpoints component:", error);
        // Keep the existing placeholder styling
      }
    }

    // Initialize Helpers component
    const helpersContainer = container.querySelector(
      ".helpers-container"
    ) as HTMLElement;
    helpersContainerEl = helpersContainer;
    if (helpersContainer) {
      try {
        const uiState = config.appStateManager?.uiState;
        const initialHelpers = uiState
          ? uiState.getState().helpers
          : ["rulers", "snap"];

        const allowedHelpers: readonly HelperType[] = [
          "rulers",
          "snap",
          "guides",
          "dots",
          "lines",
          "overlay",
        ];

        helpersAPI = createHelpersInContainer(helpersContainer, {
          onChange: (nextHelpers) => {
            if (!uiState) return;

            const normalized = nextHelpers.filter((h: HelperType) =>
              allowedHelpers.includes(h)
            );
            uiState.setHelpers(normalized as any);
            helpersAPI?.setActiveHelpers(normalized);

            const overlayAllowed =
              currentHelpersAllowed?.includes("overlay") ?? false;
            if (overlayAllowed) {
              const overlayActive = normalized.includes("overlay");
              contentMainAPI?.setVisibility({
                showBreakpoints: overlayActive,
              });
            }
          },
          initialHelpers: initialHelpers.filter((h: HelperType) =>
            allowedHelpers.includes(h)
          ) as HelperType[],
        });

        // Listen to UI state changes and update helpers display
        // Since we can't directly subscribe to UI state changes after creation,
        // we'll poll for changes or handle updates through the modal callback
        // For now, we'll rely on the initial state and manual updates through the modal
      } catch (error) {
        console.error("Failed to create Helpers component:", error);
      }
    }
  };

  /**
   * Initialize ShareContainer component
   */
  const initShareContainer = (): void => {
    const sc = container.querySelector(".share-container") as HTMLElement;
    if (!sc) return;

    try {
      sc.innerHTML = "";
      shareContainerAPI = createShareContainerInContainer(sc, {
        onRefresh: () => {
          window.location.reload();
        },
        handedness: handednessState,
      });
    } catch (error) {
      console.error("Failed to create ShareContainer:", error);
    }
  };

  // Helpers modal is handled inside Helpers component

  /**
   * Update zoom display
   * @param _zoom - New zoom percentage (unused - updateFromState reads from UI State)
   */
  const updateZoom = (_zoom: number): void => {
    if (isDestroyed) return;

    // Update ZoomControls component if available
    if (zoomControlsAPI) {
      zoomControlsAPI.updateFromState();
    }
  };

  /**
   * Update components from UI state
   */
  const updateFromState = (): void => {
    if (isDestroyed) return;

    const uiState = config.appStateManager?.uiState;
    if (!uiState) return;

    const state = uiState.getState();

    // Update zoom controls
    if (zoomControlsAPI) {
      zoomControlsAPI.updateFromState();
    }

    // Update orchestrated content block
    if (contentMainAPI) {
      contentMainAPI.update({
        selectedBreakpoint: state.current_breakpoint as any,
        selectedTheme: state.theme as any,
        selectedLanguage: state.current_language as any,
      });
    }

    // Update helpers
    if (helpersAPI) {
      const componentHelpers = (state.helpers as readonly string[]).filter(
        (helper) =>
          ["rulers", "snap", "guides", "dots", "lines", "overlay"].includes(
            helper
          )
      );
      helpersAPI.setActiveHelpers(componentHelpers as HelperType[]);

      const overlayAllowed = currentHelpersAllowed?.includes("overlay") ?? false;
      if (overlayAllowed) {
        const overlayActive = componentHelpers.includes("overlay");
        contentMainAPI?.setVisibility({
          showBreakpoints: overlayActive,
        });
      }
    }

    // Update StatusHUD from UI state
    if (statusHUDAPI) {
      statusHUDAPI.updateFromUIState();
    }
  };

  const applyVisibility = (visibility: InfoHUDVisibilityConfig): void => {
    if (isDestroyed) return;

    if (zoomContainerEl) {
      zoomContainerEl.style.display = visibility.zoom ? "" : "none";
    }

    if (statusContainerEl) {
      statusContainerEl.style.display = visibility.status ? "" : "none";
    }

    if (contentMainContainerEl) {
      const shouldShowContent =
        visibility.breakpoints.visible ||
        visibility.languages ||
        visibility.themes;
      contentMainContainerEl.style.display = shouldShowContent ? "" : "none";
      contentMainContainerEl.dataset.breakpointsVariant =
        visibility.breakpoints.variant ?? "default";
    }

    const overlayAllowed =
      !!visibility.helpers.allowed && visibility.helpers.allowed.overlay !== false;
    const activeHelpers = helpersAPI?.getActiveHelpers() ?? [];
    const overlayActive = activeHelpers.includes("overlay");

    const showBreakpoints = overlayAllowed
      ? overlayActive
      : visibility.breakpoints.visible;

    contentMainAPI?.setVisibility({
      showBreakpoints,
      showLanguages: visibility.languages,
      showThemes: visibility.themes,
    });

    if (helpersContainerEl) {
      helpersContainerEl.style.display = visibility.helpers.visible ? "" : "none";
    }

    const allowedHelpers =
      visibility.helpers.allowed &&
      Object.keys(visibility.helpers.allowed).length > 0
        ? (Object.entries(visibility.helpers.allowed)
            .filter(([, allowed]) => allowed !== false)
            .map(([key]) => key as HelperType))
        : null;

    currentHelpersAllowed = allowedHelpers ? [...allowedHelpers] : null;
    helpersAPI?.setAllowedHelpers(currentHelpersAllowed);
  };

  /**
   * Set component visibility
   * @param visible - Visibility state
   */
  const setVisible = (visible: boolean): void => {
    if (isDestroyed) return;

    container.style.visibility = visible ? "visible" : "hidden";
  };

  /**
   * Cleanup resources
   */
  const destroy = (): void => {
    if (isDestroyed) return;

    isDestroyed = true;

    // Clean up event listeners
    if (config.appStateManager?.appContext?.eventBus) {
      const eventBus = config.appStateManager.appContext.eventBus;

      if (zoomChangeListener) {
        eventBus.off("stage-transform-changed", zoomChangeListener);
        zoomChangeListener = null;
      }

      if (uiStateChangeListener) {
        eventBus.off("ui-state-changed", uiStateChangeListener);
        uiStateChangeListener = null;
      }
    }

    // Clean up components
    if (contentMainAPI) {
      contentMainAPI.destroy();
      contentMainAPI = null;
    }

    if (zoomControlsAPI) {
      zoomControlsAPI.destroy();
      zoomControlsAPI = null;
    }

    if (statusHUDAPI) {
      statusHUDAPI.destroy();
      statusHUDAPI = null;
    }

    if (helpersAPI) {
      helpersAPI.destroy();
      helpersAPI = null;
    }

    if (shareContainerAPI) {
      shareContainerAPI.destroy();
      shareContainerAPI = null;
    }

    if (unsubscribeHandedness) {
      try {
        unsubscribeHandedness();
      } catch {}
      unsubscribeHandedness = null;
    }

    rootElement = null;

    // Remove event listeners registered on creation
    if (
      config.appStateManager?.appContext?.eventBus &&
      projectChangedListener
    ) {
      config.appStateManager.appContext.eventBus.off(
        "project-changed",
        projectChangedListener
      );
      projectChangedListener = null;
    }

    container.innerHTML = "";
  };

  // Initialize component
  initInfoHUD();
  initShareContainer();

  if (handednessState && typeof handednessState.subscribe === "function") {
    unsubscribeHandedness = handednessState.subscribe((value) => {
      if (value === "left" || value === "right") {
        applyHandednessClass(value);
      }
    });
  }

  // Store event listeners for cleanup
  let zoomChangeListener: ((data: any) => void) | null = null;
  let uiStateChangeListener: ((data: any) => void) | null = null;
  let projectChangedListener: ((evt: any) => void) | null = null;

  // Listen for zoom changes from the Stage component
  if (config.appStateManager?.appContext?.eventBus) {
    const eventBus = config.appStateManager.appContext.eventBus;

    zoomChangeListener = (data: {
      zoomValue: number;
      position: any;
      transform: any;
    }) => {
      logger.trace("ui", "🎯 InfoHUD: stage transform change", {
        zoom: data.zoomValue,
      });
      updateFromState(); // Update zoom controls from UI state
    };

    // Listen for UI state changes to update all components
    uiStateChangeListener = () => {
      logger.trace("ui", "🎯 InfoHUD: UI state change");
      updateFromState(); // Update all components from UI state
    };

    eventBus.on("stage-transform-changed", zoomChangeListener);
    eventBus.on("ui-state-changed", uiStateChangeListener);

    // Listen for project changes to update available languages
    const handleProjectChanged = (evt: any) => {
      try {
        const langs = (evt?.availableLanguages as any[]) || [];
        const uiState = config.appStateManager?.uiState;
        const currentLanguage = uiState?.getState().current_language || "en";
        if (contentMainAPI) {
          contentMainAPI.update({
            availableLanguages: langs as any,
            selectedLanguage: currentLanguage as any,
          });
        }
      } catch (e) {
        console.warn("Failed to update languages on project-changed:", e);
      }
    };
    projectChangedListener = handleProjectChanged;
    eventBus.on("project-changed", handleProjectChanged);
  }

  // Return API
  return {
    updateZoom,
    updateFromState,
    applyVisibility,
    setVisible,
    destroy,
  };
};
